import { Map, Set } from 'immutable'
import { createSelector } from 'reselect'
import { getOrder, TypeAdherent } from '../../../constant/TypeAdherent'
import { estMajeurFinAnnee } from '../../../utils/dateUtils'
import FormatUtils from '../../../utils/FormatUtils'
import { userSelector } from '../../ui/user/userSelectors'

export const getAffiliation = state => state.affiliation.data
export const isAffiliationLoading = state => state.affiliation.loading
export const getExpirationDate = state => state.affiliation.expirationDate
export const getAffiliationAssurePrincipal = state => state.affiliation.data.assurePrincipal

export const getAffiliationAssureConnecte = createSelector(
	getAffiliation,
	userSelector,
	getAffiliationAssurePrincipal,
	(affiliation, user, assurePrincipal) => {
		if(user.profil.compteConjoint) {
			const beneficiaire = affiliation.beneficiaires ? affiliation.beneficiaires.filter((beneficiaire) => {
				return beneficiaire.numIndividu.toString() === user.idAdherent
			}) : []
			if(beneficiaire.length > 0){
				return beneficiaire[0]
			}
			return {}
		}
		return assurePrincipal
	}
)

export const getAffiliationBeneficiaires = createSelector(
	getAffiliation,
	(affiliation) => {
		const now = (new Date()).setHours(0, 0, 0, 0)
		return (
			affiliation.beneficiaires ? affiliation.beneficiaires.filter((beneficiaire) => {
				if (beneficiaire.garanties && beneficiaire.garanties.length > 0) {
					for (const garantie of beneficiaire.garanties) {
						const fin = garantie.dateFin ? FormatUtils.toLocalDate(garantie.dateFin) : null
						const debut = garantie.dateDebut ? FormatUtils.toLocalDate(garantie.dateDebut) : null
						if ((!fin || now <= fin) && debut && debut <= now) {
							return true
						}
					}
				}
				return false
			}) : []
		)
	}
)

export const getAffiliationBeneficiairesForRefund = createSelector(
	getAffiliation,
	(affiliation) => {
		const now = (new Date()).setHours(0, 0, 0, 0)
		return (
			affiliation.beneficiaires ? affiliation.beneficiaires.filter((beneficiaire) => {
				if (beneficiaire.garanties && beneficiaire.garanties.length > 0) {
					for (const garantie of beneficiaire.garanties) {
						const fin = garantie.dateFin ? FormatUtils.toLocalDate(garantie.dateFin) : null
						//On veut aussi les garanties dont la date de couverture date de moins de 2 ans
						fin && fin.setFullYear(fin.getFullYear() + 2)
						const debut = garantie.dateDebut ? FormatUtils.toLocalDate(garantie.dateDebut) : null

						if ((!fin || now <= fin) && debut && debut <= now) {
							return true
						}
					}
				}
				return false
			}) : []
		)
	}
)

export const getAffiliationBeneficiairesForManage = createSelector(
	getAffiliation,
	(affiliation) => {
		const now = (new Date()).setHours(0, 0, 0, 0)
		return (
			affiliation.beneficiaires ? affiliation.beneficiaires.filter((beneficiaire) => {
				if (beneficiaire.garanties && beneficiaire.garanties.length > 0) {
					for (const garantie of beneficiaire.garanties) {
						const fin = garantie.dateFin ? FormatUtils.toLocalDate(garantie.dateFin) : null

						if (!fin || now <= fin) {
							return true
						}
					}
				}
				return false
			}) : []
		)
	}
)

export const getAffiliationBeneficiairesWithoutFinFiltre = createSelector(
	getAffiliation,
	(affiliation) => {
		const now = (new Date()).setHours(0, 0, 0, 0)
		return (
			affiliation.beneficiaires ? affiliation.beneficiaires.filter((beneficiaire) => {
				if (beneficiaire.garanties && beneficiaire.garanties.length > 0) {
					return true
				}
				return false
			}) : []
		)
	}
)


/**
 * A utiliser lorsqu'on ne fait pas confiance au simple `assurePrincipal`.
 *
 * @return tous les assurés principaux des affiliations
 */
export const getTousAssuresPrincipaux = createSelector([
	getAffiliationAssurePrincipal,
	getAffiliationBeneficiaires
], (assurePrincipal, beneficiaires) => {
	return [assurePrincipal, ...beneficiaires]
		.filter(indiv => indiv && indiv.typAdr === TypeAdherent.ASSURE_PRINCIPAL)
})

export const sortIndividus = (ind1, ind2) => {
	// tri par type adhérent (assurePrincipal, Conjoint, Enfant)
	if (getOrder(ind1.typAdr) < getOrder(ind2.typAdr)) {
		return -1
	} else if (getOrder(ind1.typAdr) > getOrder(ind2.typAdr)) {
		return 1
	}
	// Tri des enfants par date de naissance
	const dateNaissanceInd1 = FormatUtils.parseDate(ind1.dateNaissance)
	const dateNaissanceInd2 = FormatUtils.parseDate(ind2.dateNaissance)

	if (dateNaissanceInd1 < dateNaissanceInd2) {
		return -1
	}
	return 1
}

export const getIndividuList = createSelector(
	getAffiliationAssurePrincipal,
	getAffiliationBeneficiaires,
	(assurePrincipal, beneficiaires) => {
		const individuMap = new Map().withMutations(map => {
			assurePrincipal && map.set(assurePrincipal.numIndividu, assurePrincipal)
			beneficiaires && beneficiaires.forEach((b) => {
				if (!map.get(b.numIndividu)) {
					map.set(b.numIndividu, b)
				}
			})
		})
		return individuMap.sort(sortIndividus).toArray()
	}
)

export const getIndividuListWithoutFinFiltre = createSelector(
	getAffiliationAssurePrincipal,
	getAffiliationBeneficiairesWithoutFinFiltre,
	(assurePrincipal, beneficiaires) => {
		const individuMap = new Map().withMutations(map => {
			assurePrincipal && map.set(assurePrincipal.numIndividu, assurePrincipal)
			beneficiaires && beneficiaires.forEach((b) => {
				if (!map.get(b.numIndividu)) {
					map.set(b.numIndividu, b)
				}
			})
		})
		return individuMap.sort(sortIndividus).toArray()
	}
)


export const getIndividuListSpecialConjoint = createSelector(
	getAffiliationAssurePrincipal,
	getAffiliationBeneficiaires,
	userSelector,
	(assurePrincipal, beneficiaires, user) => {
		const individuMap = new Map().withMutations(map => {
			if(!user.profil.compteConjoint) {
				assurePrincipal && map.set(assurePrincipal.numIndividu, assurePrincipal)
			}
			beneficiaires && beneficiaires.forEach((b) => {
				if (!map.get(b.numIndividu)) {
					map.set(b.numIndividu, b)
				}
			})
		})
		return individuMap.sort(sortIndividus).toArray()
	}
)

export const getIndividuListForRefund = createSelector(
	getAffiliationAssurePrincipal,
	getAffiliationBeneficiairesForRefund,
	(assurePrincipal, beneficiaires) => {
		const individuMap = new Map().withMutations(map => {
			assurePrincipal && map.set(assurePrincipal.numIndividu, assurePrincipal)
			beneficiaires && beneficiaires.forEach((b) => {
				if (!map.get(b.numIndividu)) {
					map.set(b.numIndividu, b)
				}
			})
		})
		return individuMap.sort(sortIndividus).toArray()
	}
)

export const getIndividuListForManage = createSelector(
	getAffiliationAssurePrincipal,
	getAffiliationBeneficiairesForManage,
	(assurePrincipal, beneficiaires) => {
		const individuMap = new Map().withMutations(map => {
			assurePrincipal && map.set(assurePrincipal.numIndividu, assurePrincipal)
			beneficiaires && beneficiaires.forEach((b) => {
				if (!map.get(b.numIndividu)) {
					map.set(b.numIndividu, b)
				}
			})
		})
		return individuMap.sort(sortIndividus).toArray()
	}
)

export const getIndividuListForManageConjoint = createSelector(
	getAffiliationAssurePrincipal,
	getAffiliationBeneficiairesForManage,
	userSelector,
	(assurePrincipal, beneficiaires, user) => {
		const individuMap = new Map().withMutations(map => {
			assurePrincipal && map.set(assurePrincipal.numIndividu, assurePrincipal)
			beneficiaires && beneficiaires.forEach((b) => {
				if (!map.get(b.numIndividu) && b.numIndividu.toString() !== user.idAdherent) {
					map.set(b.numIndividu, b)
				}
			})
		})
		return individuMap.sort(sortIndividus).toArray()
	}
)

export const getEnfantMajeurList = createSelector(
	getIndividuList,
	(individuList) =>
		(
			individuList && individuList.filter(individu => {
				return ((individu[1].typAdr === TypeAdherent.ENFANTS || individu[1].typAdr === TypeAdherent.ENFANT_INVALIDE || individu[1].typAdr === TypeAdherent.ENFANT_PRIMO_EMPLOI)
					&& estMajeurFinAnnee(individu[1].dateNaissance))
			})
		)
)

export const getConjoint = createSelector(
	getIndividuListWithoutFinFiltre,
	getIndividuList,
	userSelector,
	(individuListWithoutFinFiltre, individuList, user) => {
		if(user.affiliationExpired) {
			return individuListWithoutFinFiltre && individuListWithoutFinFiltre.find(individu => {
				return ((individu[1].typAdr === TypeAdherent.CONJOINT
						|| individu[1].typAdr === TypeAdherent.CONCUBIN
						|| individu[1].typAdr === TypeAdherent.PACSE)
					&& individu[1].infoAdh1 !== "ADHESION CROISEE" )
			})
		} else {
			return individuList && individuList.find(individu => {
				return ((individu[1].typAdr === TypeAdherent.CONJOINT
						|| individu[1].typAdr === TypeAdherent.CONCUBIN
						|| individu[1].typAdr === TypeAdherent.PACSE)
					&& individu[1].infoAdh1 !== "ADHESION CROISEE" )
			})
		}
	}
)

export const getPorteursRib = createSelector([
	getIndividuList
], (individus) => {
	return new Set().withMutations(_set => {
		individus.forEach(i => {
			if (i[1].porteurRib) {
				_set.add(i[1].porteurRib)
			}
		})
	})
})

export const getContrat = createSelector(
	getAffiliation,
	(affiliation) => {
		if (affiliation) {
			if (affiliation.assurePrincipal) {
				return affiliation.assurePrincipal.garanties
			}
		}
		return undefined
	}
)
